import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../basic';

interface SignupSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  teamName: string;
}

const SignupSuccessModal: React.FC<SignupSuccessModalProps> = ({ isOpen, onClose, email, teamName }) => {
  const navigate = useNavigate();

  const handleGoToLogin = () => {
    onClose();
    navigate('/login');
  };

  return (
    <Modal isOpen={isOpen} onClose={() => {}} maxWidth="lg" showCloseButton={false}>
      <div className="w-full rounded-lg overflow-hidden">
        <div className="max-h-[min(85dvh,85vh)] overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-green-600 via-green-700 to-emerald-700 p-6 sm:p-8 text-center relative">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute top-4 left-8 w-3 h-3 bg-white/20 rounded-full"></div>
            <div className="absolute top-8 right-12 w-2 h-2 bg-white/30 rounded-full"></div>
            <div className="absolute bottom-6 left-16 w-4 h-4 bg-white/10 rounded-full"></div>
            <div className="absolute bottom-4 right-8 w-5 h-5 bg-white/15 rounded-full"></div>
          </div>

          <div className="relative z-10">
            {/* Success Icon */}
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="absolute inset-0 bg-white rounded-full blur-lg opacity-30 scale-110"></div>
                <div className="relative bg-white rounded-full p-4 shadow-lg">
                  <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            </div>

            <h1 className="text-2xl font-bold text-white drop-shadow-lg mb-2">Account Created Successfully!</h1>
            <p className="text-green-100 text-lg">Welcome to STATDOG, {teamName}!</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8">
          {/* Email Sent Notice */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200/50 rounded-xl p-5 mb-6">
            <div className="flex items-start gap-4">
              <div className="bg-blue-500 text-white rounded-xl w-12 h-12 flex items-center justify-center text-2xl flex-shrink-0">
                📧
              </div>
              <div>
                <h3 className="font-bold text-blue-900 mb-1">Welcome Email Sent!</h3>
                <p className="text-blue-800 text-sm leading-relaxed">
                  We've sent a welcome email to <strong>{email}</strong> with important setup information and training
                  resources to help you get started.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-6">
            <h3 className="font-bold text-emerald-900 mb-2">Next Step</h3>
            <p className="text-emerald-800 text-sm leading-relaxed">
              After you log in, go to the <strong>Billing & Renewals</strong> page to complete your subscription
              setup and manage your payment details.
            </p>
          </div>

          {/* Action Button */}
          <button
            onClick={handleGoToLogin}
            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 rounded-xl font-bold text-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl transform"
          >
            Go to Login
          </button>
        </div>
        </div>
      </div>
    </Modal>
  );
};

export default SignupSuccessModal;
